// features/projects/templates/continuum/cascade-frameworks.js
// ═══════════════════════════════════════════════════════════
// 17-DIMENSION FRAMEWORK BROWSER — Section 3 of Cascade tab
//
// Renders the three populated 17-dim framework instances
// (Korea, cod, Iran) as a side-by-side comparative table,
// grouped by band (Setup / State / Mechanism / Action).
//
// Data: lib/cascade/frameworks.js
// Used by: features/projects/templates/continuum/cascade.js
// ═══════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import { C, FONT as F } from './tokens';
import { FRAMEWORK_BANDS, FRAMEWORKS } from '../../../../lib/cascade/frameworks';

export default function CascadeFrameworks() {
  const [visibleIds, setVisibleIds] = useState(() => new Set(FRAMEWORKS.map((f) => f.id)));
  const [collapsedBands, setCollapsedBands] = useState(() => new Set());

  const visibleCases = useMemo(
    () => FRAMEWORKS.filter((f) => visibleIds.has(f.id)),
    [visibleIds]
  );

  const toggleCase = (id) => {
    setVisibleIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      // never allow zero cases visible
      if (next.size === 0) return cur;
      return next;
    });
  };

  const toggleBand = (bandId) => {
    setCollapsedBands((cur) => {
      const next = new Set(cur);
      if (next.has(bandId)) next.delete(bandId);
      else next.add(bandId);
      return next;
    });
  };

  return (
    <section style={{ marginBottom: 48 }}>
      <Header />
      <CaseFilter cases={FRAMEWORKS} visibleIds={visibleIds} onToggle={toggleCase} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {FRAMEWORK_BANDS.map((band) => (
          <BandSection
            key={band.id}
            band={band}
            cases={visibleCases}
            collapsed={collapsedBands.has(band.id)}
            onToggle={() => toggleBand(band.id)}
          />
        ))}
      </div>
    </section>
  );
}

// ─── Header ─────────────────────────────────────────────────────────
function Header() {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em' }}>03</span>
        <span style={{ fontFamily: F.serif, fontSize: 22, fontWeight: 400, color: C.text }}>17-Dimension Framework</span>
        <span style={{ fontSize: 10, color: C.static, fontFamily: F.mono, letterSpacing: '0.04em', marginLeft: 6 }}>
          {FRAMEWORKS.length} populated cases · 4 bands · 17 dimensions
        </span>
      </div>
      <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.5, maxWidth: 800 }}>
        Substrate-symmetric comparative framework. Each case carries values across the same 17 dimensions. The Setup band must be matched between paired cases before State-band analysis is permitted.
      </div>
    </div>
  );
}

// ─── Case filter ────────────────────────────────────────────────────
function CaseFilter({ cases, visibleIds, onToggle }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em', alignSelf: 'center', marginRight: 4 }}>
        SHOW:
      </span>
      {cases.map((c) => {
        const visible = visibleIds.has(c.id);
        const color = C[c.colorKey] || C.gold;
        return (
          <button
            key={c.id}
            onClick={() => onToggle(c.id)}
            style={{
              fontSize: 10,
              fontFamily: F.mono,
              letterSpacing: '0.06em',
              padding: '5px 12px',
              borderRadius: 3,
              border: `1px solid ${visible ? color : C.b2}`,
              background: visible ? hexAlpha(color, 0.1) : 'transparent',
              color: visible ? color : C.static,
              cursor: 'pointer',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span>{c.label}</span>
            <span style={{ fontSize: 9, color: visible ? color : C.dim }}>
              {c.substrate} · {c.period}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Band Section (collapsible) ──────────────────────────────────────
function BandSection({ band, cases, collapsed, onToggle }) {
  return (
    <div
      style={{
        background: C.s2,
        border: `1px solid ${C.b1}`,
        borderRadius: 4,
        overflow: 'hidden',
      }}
    >
      <button
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          width: '100%',
          padding: '12px 16px',
          background: C.s1,
          border: 'none',
          borderBottom: collapsed ? 'none' : `1px solid ${C.b1}`,
          color: C.text,
          textAlign: 'left',
          cursor: 'pointer',
          fontFamily: F.sans,
        }}
      >
        <span style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em', minWidth: 24 }}>
          {collapsed ? '▸' : '▾'}
        </span>
        <span style={{ fontFamily: F.serif, fontSize: 18, fontWeight: 400, color: C.text }}>
          {band.label} band
        </span>
        <span style={{ fontSize: 10, color: C.static, fontFamily: F.mono, letterSpacing: '0.04em' }}>
          {band.dimensions.length} dimensions
        </span>
        <span style={{ fontSize: 11, color: C.mid, marginLeft: 'auto', fontStyle: 'italic' }}>
          {band.description}
        </span>
      </button>

      {!collapsed && (
        <div style={{ padding: '8px 0' }}>
          {band.dimensions.map((dim, idx) => (
            <DimensionRow
              key={dim.id}
              dim={dim}
              cases={cases}
              isLast={idx === band.dimensions.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Dimension Row — one dimension across all visible cases ─────────
function DimensionRow({ dim, cases, isLast }) {
  // Grid columns: dimension label (240px) + one column per visible case
  const gridTemplate = `240px repeat(${cases.length}, 1fr)`;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: gridTemplate,
        gap: 0,
        borderBottom: isLast ? 'none' : `1px solid ${C.b1}`,
      }}
    >
      <div
        style={{
          padding: '12px 16px',
          background: C.s1,
          borderRight: `1px solid ${C.b1}`,
        }}
      >
        <div style={{ fontSize: 12, color: C.text, fontFamily: F.sans, fontWeight: 500, marginBottom: 4 }}>
          {dim.label}
        </div>
        <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, lineHeight: 1.4 }}>
          {dim.short}
        </div>
      </div>

      {cases.map((c, idx) => {
        const value = c.values[dim.id];
        const color = C[c.colorKey] || C.gold;
        const isLastCol = idx === cases.length - 1;
        return (
          <div
            key={c.id}
            style={{
              padding: '12px 16px',
              borderRight: isLastCol ? 'none' : `1px solid ${C.b1}`,
              fontSize: 12,
              color: C.text,
              lineHeight: 1.55,
              fontFamily: F.sans,
            }}
          >
            <div
              style={{
                fontSize: 9,
                color: color,
                fontFamily: F.mono,
                letterSpacing: '0.06em',
                marginBottom: 6,
                textTransform: 'uppercase',
              }}
            >
              {c.label}
            </div>
            <div style={{ color: C.mid }}>
              {value || <span style={{ color: C.dim, fontStyle: 'italic' }}>(no value)</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function hexAlpha(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
